import React, { useState, useEffect } from "react";
import axios from "axios";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
  Button,
  Breadcrumb,
  Card,
  Select,
  DatePicker
} from "../UI";
import { Formik } from "formik";
import { Users, ChevronLeft, Calendar, Info, PlusCircle } from "lucide-react";
import { axiosConfig } from "../../config/index";
import { addDelegateAdmin } from "../../store/actions";
import { useToast } from "../UI";

import { useDebounce } from "../../hooks/useDebounce";
import { withRouter } from "react-router-dom"; // using withRouter for navigation

const AddDelegationAdmin = ({ addDelegateAdmin, user, history }: any) => {
  const { toast } = useToast();
  // history will be provided via props by withRouter
  const [delegatorItems, setDelegatorItems] = useState<any>([]);
  const [delegateItems, setDelegateItems] = useState<any>([]);
  const [delegatorSearch, setDelegatorSearch] = useState<string>("");
  const [delegateSearch, setDelegateSearch] = useState<string>("");
  const [submitError, setSubmitError] = useState<string>('');

  const debouncedDelegatorSearch = useDebounce(delegatorSearch, 500);
  const debouncedDelegateSearch = useDebounce(delegateSearch, 500);

  // Initialize with current user
  useEffect(() => {
    if (user?.oracleId) {
      const initialItem = {
        label: user.fullName ? `${user.oracleId} - ${user.fullName}` : user.oracleId,
        value: user.oracleId
      };
      setDelegatorItems([initialItem]);
      setDelegateItems([initialItem]);
    }
  }, [user]);

  // Delegator Search logic
  useEffect(() => {
    if (debouncedDelegatorSearch) {
      axios.get(`/api/requests/search/${debouncedDelegatorSearch}`, axiosConfig)
        .then((res) => {
          const newItems = res.data.map((emp: any) => ({
            label: `${emp.oracleId} - ${emp.name}`,
            value: emp.oracleId
          }));
          setDelegatorItems(newItems);
        })
        .catch(err => console.error("Search delegator error:", err));
    }
  }, [debouncedDelegatorSearch]);

  // Delegate Search logic
  useEffect(() => {
    if (debouncedDelegateSearch && debouncedDelegateSearch.trim().length > 0) {
      axios.get(`/api/requests/search/${debouncedDelegateSearch}`, axiosConfig)
        .then((res) => {
          const newItems = res.data.map((emp: any) => ({
            label: `${emp.oracleId} - ${emp.name}`,
            value: emp.oracleId
          }));
          setDelegateItems(newItems);
        })
        .catch(err => console.error("Search delegate error:", err));
    } else {
      setDelegateItems([]);
    }
  }, [debouncedDelegateSearch]);

  return (
    <div className="max-w-2xl space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col gap-4">
        <Breadcrumb
          items={[
            { label: 'Home', to: '/dashboard' },
            { label: 'Delegation List', to: '/delegationlistbyadmin' },
            { label: 'Add Delegation' }
          ]}
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/delegationlistbyadmin">
              <Button variant="ghost" size="sm" className="rounded-full p-2 h-10 w-10 border-none shadow-none">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <div className="bg-white p-4 flex items-center gap-4">

                <div className="p-2 bg-[#8dc63f] rounded-2xl shadow-lg shadow-[#8dc63f]/30">

                  <Users className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight leading-none">Add Delegation</h1>

              </div>
            </div>
          </div>
        </div>
      </div>

      <Card className="p-0 border-red shadow-2xl shadow-slate-200/50 overflow-hidden">
        <div className="p-4 bg-white mt-[-10px] rounded-t-[32px]">
          <Formik
            enableReinitialize={true}
            initialValues={{
              delegatedBy: user?.oracleId || "",
              delegatedTo: "",
              startDate: "",
              endDate: ""
            }}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              if (!values.delegatedBy || !values.delegatedTo || !values.startDate || !values.endDate) {
                toast("Please fill all required fields", "warning");
                setSubmitting(false);
                return;
              }
              try {
                await addDelegateAdmin(values);
                toast("Delegation added successfully", "success");
                resetForm();
                setSubmitError("");
                // Navigate based on role after successful addition
                if (user?.isAdmin || (user?.roleIds && user?.roleIds.length > 0) || (user?.jobRole && /manager|officer|supervisor/i.test(user.jobRole))) {
                  history.push("/delegationlistbyadmin");
                } else {
                  history.push("/dashboard");
                }
              } catch (error: any) {
                const errMsg = error.delegate || error.message || "Failed to add delegation";
                setSubmitError(errMsg);
                toast(errMsg, "error");
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ handleSubmit, setFieldValue, isSubmitting, values, errors, touched }) => (
              <form onSubmit={handleSubmit} className="space-y-8 max-w-2xl mx-auto pt-4 pb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <Select
                      label="Delegated By"
                      placeholder="Search employee..."
                      options={delegatorItems}
                      onSearch={(val) => setDelegatorSearch(val)}
                      onChange={(val) => setFieldValue("delegatedBy", val)}
                      value={values.delegatedBy}
                      required
                      error={touched.delegatedBy && errors.delegatedBy ? String(errors.delegatedBy) : undefined}
                    />

                    <Select
                      label="Delegated To"
                      placeholder="Search employee..."
                      /* Filter out the delegator right here in JSX where `values` is available */
                      options={delegateItems.filter((item: any) => item.value !== values.delegatedBy)}
                      onSearch={(val) => setDelegateSearch(val)}
                      onChange={(val) => setFieldValue("delegatedTo", val)}
                      value={values.delegatedTo}
                      required
                      error={touched.delegatedTo && errors.delegatedTo ? String(errors.delegatedTo) : undefined}
                    />
                  </div>

                  <div className="space-y-6">
                    <DatePicker
                      label="Start Date"
                      value={values.startDate}
                      onChange={(e) => setFieldValue("startDate", e.target.value)}
                      required
                    />

                    <DatePicker
                      label="End Date"
                      value={values.endDate}
                      onChange={(e) => setFieldValue("endDate", e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-center justify-end gap-3 pt-4 border-t border-slate-50">
                  <Button
                    type="submit"
                    className="h-10 px-4 gap-1.5 text-xs"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    Delegate
                  </Button>
                  <Link to="/delegationlistbyadmin">
                    <Button variant="outline" className="h-9 px-4 gap-1.5 text-xs">Cancel</Button>
                  </Link>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </Card>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.auth.user,
});

const mapDispatchToProps = {
  addDelegateAdmin,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(AddDelegationAdmin));



